if (!customElements.get('hyc-product-story')) {
  customElements.define('hyc-product-story', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      const one = selector => this.querySelector(selector);
      const all = selector => [...this.querySelectorAll(selector)];
      const on = (target, event, callback, options = {}) => target.addEventListener(event, callback, { ...options, signal });
      const closingForm = one('[data-hyc-closing-form]');
      const mainQuantitySelector = 'product-info.hyc-buy-main input[name="quantity"]';
      const closingQuantitySelector = '.hyc-closing-form input[name="quantity"]';
      on(document, 'change', event => {
        const source = event.target;
        if (!(source instanceof HTMLInputElement)) return;
        let target;
        if (source.matches(mainQuantitySelector)) target = one('input[name="quantity"]');
        else if (source.matches(closingQuantitySelector) && this.contains(source)) target = document.querySelector(mainQuantitySelector);
        if (!target || !source.checkValidity() || target.value === source.value) return;
        target.value = source.value;
        target.dispatchEvent(new Event('change', { bubbles: true }));
      });
      if (closingForm) {
        this.purchaseObserver = new IntersectionObserver(entries => {
          document.body.classList.toggle('hyc-final-buy-visible', entries.some(entry => entry.isIntersecting));
        });
        this.purchaseObserver.observe(closingForm.querySelector('[type="submit"]'));
      }
      const compact = () => this.dataset.storyVariant === 'shorter';
      const stage = one('.product-stage');
      if (!stage) return;
      const steps = all('.story-step');
      const buttons = all('[data-step]');
      const video = one('.motion-concept');
      const toggle = one('.motion-toggle');
      const reduced = matchMedia('(prefers-reduced-motion: reduce)');
      const mobile = matchMedia('(max-width: 760px)');
      const figures = ['20″', '62cc', '6.9kg'];
      let active = 0, enabled = true, ready = false, desired = 1;
      this.classList.add('is-enhanced');

      const revealItems = all('.section-intro, .ownership-heading, .kit-copy, .hyc-film-heading, .hyc-film-card, .hyc-film-topics > div, .support > div, .spec-heading, .closing');
      const designMotion = this.dataset.designMotion === 'true';
      if (designMotion && !reduced.matches) {
        this.classList.add('has-design-motion');
        this.revealObserver = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-seen');
              this.revealObserver.unobserve(entry.target);
            }
          });
        }, { threshold: 0.08 });
        revealItems.forEach((item, index) => {
          item.classList.add('hyc-reveal');
          item.style.setProperty('--reveal-delay', `${index % 3 * 70}ms`);
          this.revealObserver.observe(item);
        });
      }
      on(reduced, 'change', () => {
        if (reduced.matches) {
          this.classList.remove('has-design-motion');
          this.revealObserver?.disconnect();
          revealItems.forEach(item => item.classList.add('is-seen'));
        }
      });
      all('[data-film-launch]').forEach(link => on(link, 'click', event => {
        const card = link.closest('[data-film-card]');
        const template = card.querySelector('[data-film-player]');
        if (!template?.content.firstElementChild) return;
        event.preventDefault();
        all('.hyc-film-player').forEach(player => {
          if (player.tagName === 'VIDEO') player.pause();
          else if (player.tagName === 'IFRAME') {
            const previous = player.closest('[data-film-card]');
            player.remove();
            previous.querySelector('[data-film-launch]').hidden = false;
          }
        });
        const player = template.content.firstElementChild.cloneNode(true);
        card.querySelector('[data-film-screen]').append(player);
        link.hidden = true;
        player.tabIndex = 0;
        player.focus({ preventScroll: true });
        if (player.tagName === 'VIDEO') player.play().catch(() => {});
      }));

      const setStep = index => {
        active = index;
        stage.dataset.state = String(index);
        one('.giant-stat').textContent = figures[index];
        one('.component-label').textContent = steps[index].querySelector('.key-figure span').textContent.replace(/^\s*\/\s*/, '');
        one('[data-stage-count]').textContent = `0${index + 1} / 03`;
        steps.forEach((step, i) => step.classList.toggle('active', i === index));
        buttons.forEach((button, i) => button.setAttribute('aria-pressed', String(i === index)));
      };
      const seek = time => {
        if (!video || !Number.isFinite(video.duration)) return;
        desired = Math.max(0, Math.min(video.duration - 0.08, time));
        if (ready && enabled && !reduced.matches && !video.seeking && Math.abs(video.currentTime - desired) > 0.06) video.currentTime = desired;
      };
      const ownership = one('.ownership');
      if (designMotion && !reduced.matches) {
        this.entryObserver = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) {
            ownership.classList.add('is-entry-revealed');
            this.entryObserver.disconnect();
          }
        }, { threshold: 0.5 });
        this.entryObserver.observe(one('.hyc-ownership-entry'));
      }
      const readingProgress = one('.reading-progress');
      const storyElement = one('.product-story');
      const sync = () => {
        if (this.raf) return;
        this.raf = requestAnimationFrame(() => {
          this.raf = 0;
          const rect = this.getBoundingClientRect();
          if (rect.bottom < 0 || rect.top > innerHeight) return;
          if (compact()) return;
          const stageBox = stage.getBoundingClientRect();
          const progressWidth = `${Math.max(0, Math.min(100, -rect.top / Math.max(1, rect.height - innerHeight) * 100))}%`;
          if (stageBox.bottom < -100 || stageBox.top > innerHeight + 100) {
            readingProgress.style.width = progressWidth;
            return;
          }
          const storyBox = storyElement.getBoundingClientRect();
          const centres = steps.map(step => { const r = step.getBoundingClientRect(); return r.top + r.height / 2; });
          // Follow the visible product stage, rather than a fixed percentage of the whole browser.
          const focusY = mobile.matches ? innerHeight * 0.55 : stageBox.top + stageBox.height / 2;
          if (!mobile.matches) {
            const distances = centres.map(y => Math.abs(y - focusY));
            const next = distances.indexOf(Math.min(...distances));
            if (next !== active) setStep(next);
          }
          // A compact story may have steps shorter than the pinned stage. Use the
          // last reachable stage centre so the turn still reaches its final frame.
          const endCentre = Math.min(centres[2], storyBox.bottom - stageBox.height / 2);
          const travel = Math.max(1, endCentre - centres[0]);
          const progress = Math.max(0, Math.min(1, (focusY - centres[0]) / travel));
          seek(mobile.matches ? [1, 4.5, 8][active] : 1 + progress * 7);
          readingProgress.style.width = progressWidth;
        });
      };
      buttons.forEach((button, index) => on(button, 'click', () => {
        setStep(index);
        if (!mobile.matches) steps[index].scrollIntoView({ block: 'center', behavior: reduced.matches ? 'instant' : 'smooth' });
        sync();
      }));
      const detailButtons = all('[data-detail]');
      const chooseDetail = button => {
        if (compact()) return;
        const key = button.dataset.detail;
        detailButtons.forEach(item => {
          const selected = item === button;
          item.setAttribute('aria-selected', String(selected));
          item.tabIndex = selected ? 0 : -1;
          this.querySelector(`[id="${item.getAttribute('aria-controls')}"]`).hidden = !selected;
        });
        const data = this.querySelector(`[data-detail-asset="${key}"]`).dataset;
        one('[data-detail-photo]').srcset = data.srcset || '';
        one('[data-detail-photo]').src = data.src;
        one('[data-detail-photo]').alt = data.alt;
        one('[data-detail-caption]').textContent = data.caption;
        one('.detail-image').className = `detail-image ${key}`;
      };
      detailButtons.forEach((button, i) => {
        on(button, 'click', () => chooseDetail(button));
        on(button, 'keydown', event => {
          const next = { ArrowRight: (i + 1) % 3, ArrowLeft: (i + 2) % 3, Home: 0, End: 2 }[event.key];
          if (next !== undefined) { event.preventDefault(); detailButtons[next].focus(); chooseDetail(detailButtons[next]); }
        });
      });
      chooseDetail(detailButtons[0]);
      // Selecting a copy block in Shopify should reveal the content being edited.
      if (window.Shopify?.designMode) {
        on(this, 'shopify:block:select', event => {
          const target = event.target;
          if (!(target instanceof HTMLElement)) return;
          const panel = target.closest('[role="tabpanel"]');
          const tab = panel && detailButtons.find(button => button.getAttribute('aria-controls') === panel.id);
          if (tab) chooseDetail(tab);
          if (target.matches('details')) target.open = true;
          target.classList.add('is-seen');
          target.querySelectorAll('.hyc-reveal').forEach(item => item.classList.add('is-seen'));
          if (target.closest('.ownership')) ownership.classList.add('is-entry-revealed');
          target.scrollIntoView({ behavior: 'auto', block: 'center' });
        });
      }
      all('a[href="#judgeme_product_reviews"]').forEach(link => on(link, 'click', event => {
        const reviews = document.getElementById('judgeme_product_reviews');
        if (!reviews) return;
        event.preventDefault();
        // Avoid the review app's document-level jump, which ignores our sticky navigation.
        event.stopPropagation();
        reviews.scrollIntoView({ behavior: reduced.matches ? 'instant' : 'smooth', block: 'start' });
      }));
      all('[data-buy-link]').forEach(link => on(link, 'click', event => {
        const product = document.getElementById('hyc-buying-options');
        if (!product) return;
        event.preventDefault();
        product.scrollIntoView({ behavior: reduced.matches ? 'instant' : 'smooth', block: 'start' });
        product.focus({ preventScroll: true });
      }));
      if (video) {
        const refreshMotion = () => {
          stage.classList.toggle('motion-concept-on', ready && enabled && !reduced.matches);
          toggle.hidden = !ready || reduced.matches;
          sync();
        };
        on(video, 'loadeddata', () => { ready = true; refreshMotion(); });
        on(video, 'seeked', () => seek(desired));
        on(video, 'error', () => { ready = false; refreshMotion(); });
        on(toggle, 'click', () => {
          enabled = !enabled;
          toggle.textContent = enabled ? toggle.dataset.photoLabel : toggle.dataset.motionLabel;
          toggle.setAttribute('aria-pressed', String(enabled));
          refreshMotion();
        });
        this.mediaObserver = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting) && !reduced.matches && !compact()) {
            video.src = video.dataset.videoSrc;
            video.load();
            this.mediaObserver.disconnect();
          }
        }, { rootMargin: '400px' });
        this.mediaObserver.observe(stage);
        on(reduced, 'change', () => {
          if (!reduced.matches && !video.getAttribute('src')) this.mediaObserver.observe(stage);
          refreshMotion();
        });
      }
      on(window, 'scroll', sync, { passive: true });
      on(window, 'resize', sync, { passive: true });
      setStep(0);
      sync();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.mediaObserver?.disconnect();
      this.revealObserver?.disconnect();
      cancelAnimationFrame(this.raf);
      this.entryObserver?.disconnect();
      this.purchaseObserver?.disconnect();
      document.body.classList.remove('hyc-final-buy-visible');
      this.raf = 0;
    }
  });
}
